import { useMemo, useRef, useState } from "react";
import { ImageIcon, Plus, Upload, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import type { Asset } from "@/lib/types";
import { cn } from "@/lib/utils";

export interface Reference {
  asset_id: string;
  thumbnail_url: string;
  file_url: string;
  name: string;
}

interface Props {
  references: Reference[];
  /** All eligible assets the user can pick from. Filtered inside this
   *  component to source_product_image + generated_image (i.e. the user's
   *  own images, not lookups). */
  libraryAssets: Asset[];
  /** Max upload size in MB. Determined by the chosen model's capability and
   *  passed in from the parent (FreeView). 0 = unknown / unlimited UI hint. */
  maxUploadMB: number;
  onRemove: (assetId: string) => void;
  onPickFromLibrary: (asset: Reference) => void;
  /** Called with the dropped/picked File. Implementation handles upload + asset register. */
  onUpload: (file: File) => Promise<void> | void;
}

export function ReferenceStrip({
  references,
  libraryAssets,
  maxUploadMB,
  onRemove,
  onPickFromLibrary,
  onUpload,
}: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const cap = maxUploadMB > 0 ? maxUploadMB : 10;

  function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("只支持图片文件");
      return;
    }
    if (file.size > cap * 1024 * 1024) {
      alert(`文件超过 ${cap} MB（当前模型上限）`);
      return;
    }
    onUpload(file);
  }

  // Eligible assets the user can pick: their own uploads + previously
  // generated images, excluding any already added as a reference.
  const eligible = useMemo(
    () =>
      libraryAssets.filter(
        (a) =>
          (a.asset_type === "source_product_image" ||
            a.asset_type === "generated_image") &&
          !references.some((r) => r.asset_id === a.asset_id),
      ),
    [libraryAssets, references],
  );

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground/85">
        参考图（可选）
      </p>
      <div
        className={cn(
          "flex flex-wrap items-center gap-2 rounded-md p-1 transition-colors",
          isDragging && "bg-accent/10 outline outline-2 outline-dashed outline-accent",
        )}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        {references.map((ref) => (
          <div
            key={ref.asset_id}
            className="relative h-16 w-16 overflow-hidden rounded-md border border-border"
          >
            <img
              src={ref.thumbnail_url || ref.file_url}
              alt={ref.name}
              loading="lazy"
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget.parentElement as HTMLElement).style.display = "none";
              }}
            />
            <button
              type="button"
              aria-label={`移除 ${ref.name}`}
              onClick={() => onRemove(ref.asset_id)}
              className="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-foreground backdrop-blur transition-colors hover:bg-black/85"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-16 min-w-[120px] flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-border bg-card px-3 text-xs text-muted-foreground transition-colors hover:border-accent hover:bg-accent/10 hover:text-accent"
        >
          <Upload className="h-4 w-4" />
          <span className="font-medium">拖拽上传</span>
          <span className="text-[10px] text-muted-foreground/60">或点击选择</span>
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-border bg-card text-xs text-muted-foreground transition-colors hover:border-accent hover:bg-accent/10 hover:text-accent"
        >
          <Plus className="h-4 w-4" />
          <span>从库选</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      <p className="text-[11px] text-muted-foreground/70">
        支持 JPG / PNG / WebP，单图 &lt; {cap} MB
        {maxUploadMB > 0 ? "（当前模型上限）" : ""}。
      </p>

      <LibraryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        assets={eligible}
        onPick={(a) => {
          onPickFromLibrary(a);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}

function LibraryPicker({
  open,
  onClose,
  assets,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  assets: Asset[];
  onPick: (ref: Reference) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-[min(720px,90vw)] overflow-hidden p-0">
        <div className="border-b border-border px-5 py-4">
          <DialogTitle>从素材库选参考图</DialogTitle>
          <DialogDescription>
            点击想要的图片即可添加到当前任务。
          </DialogDescription>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {assets.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <div className="grid h-14 w-14 place-items-center rounded-full border border-border bg-muted text-muted-foreground">
                <ImageIcon className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-foreground">素材库为空</p>
              <p
                className="max-w-[280px] text-xs text-muted-foreground"
                style={{ textWrap: "balance" }}
              >
                先去「素材库」上传一张商品图，或先生成几张作品再来。
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {assets.map((a) => (
                <button
                  key={a.asset_id}
                  type="button"
                  onClick={() =>
                    onPick({
                      asset_id: a.asset_id,
                      thumbnail_url: a.thumbnail_url,
                      file_url: a.file_url || a.thumbnail_url,
                      name: a.name,
                    })
                  }
                  className="group flex flex-col gap-1 overflow-hidden rounded-md border border-border bg-card text-left transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-md"
                  title={a.name}
                >
                  <div className="aspect-square overflow-hidden bg-muted">
                    <img
                      src={a.thumbnail_url || a.file_url}
                      alt={a.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform group-hover:scale-[1.04]"
                      onError={(e) => {
                        (e.currentTarget.parentElement as HTMLElement).style.display = "none";
                      }}
                    />
                  </div>
                  <span className="truncate px-2 pb-2 text-xs text-foreground/85">
                    {a.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
